import React from 'react';
import { useNavigate } from 'react-router-dom';

const WebinarUpsellModal = ({ isOpen, onClose, webinarUrl }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpsellClick = (productId) => {
    onClose();
    navigate(`/checkout?product=${productId}`);
  };

  const handleSkipToWebinar = () => {
    onClose();
    window.open(webinarUrl, '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border-4 border-yellow-400 animate-slideUp">
        {/* Attention-Grabbing Header */}
        <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400 p-8 text-center relative">
          <button
            onClick={handleSkipToWebinar}
            className="absolute top-4 right-4 bg-black/20 hover:bg-black/40 text-white rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold transition-all"
            aria-label="Close"
          >
            ×
          </button>
          
          <div className="mb-4">
            <span className="inline-block bg-red-600 text-white px-6 py-2 rounded-full text-sm font-black uppercase animate-pulse">
              ⚡ Special Offer ⚡
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-3">
            WAIT! 🛑
          </h2>
          <h3 className="text-3xl font-bold text-gray-900 mb-3">
            You May Want This Too...
          </h3>
          <p className="text-gray-800 text-xl font-semibold max-w-2xl mx-auto">
            Skip the wait! Get <span className="underline decoration-red-600 decoration-4">instant personalized help</span> instead of waiting for the group webinar
          </p>
        </div>

        {/* Content */}
        <div className="p-8 bg-gray-900">
          <div className="text-center mb-8">
            <p className="text-yellow-400 text-2xl font-bold mb-2">👇 Choose Your Fast-Track Option 👇</p>
            <p className="text-gray-300 text-lg">Get the answers you need TODAY, not next week</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            {/* Pick My Brain Upsell */}
            <div className="bg-gradient-to-br from-blue-800 to-blue-900 rounded-2xl p-8 border-4 border-blue-500 relative transform hover:scale-105 transition-all shadow-2xl">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-6 py-2 rounded-full text-sm font-black uppercase shadow-lg animate-bounce">
                  🔥 Most Popular 🔥
                </span>
              </div>
              <div className="text-center mb-6 mt-4">
                <h3 className="text-3xl font-black text-white mb-3">Pick My Brain Power Session</h3>
                <p className="text-blue-200 mb-4 text-lg">Get answers to YOUR specific questions in a private 1-on-1 call</p>
                <div className="bg-blue-600/30 rounded-lg p-4 mb-4">
                  <div className="text-5xl font-black text-yellow-400 mb-2">$99</div>
                  <p className="text-blue-100 text-sm">30 Minutes • Book Anytime</p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6 text-sm">
                <li className="flex items-start text-gray-200">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span>One-on-one personalized consultation</span>
                </li>
                <li className="flex items-start text-gray-200">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span>Get answers to YOUR specific questions</span>
                </li>
                <li className="flex items-start text-gray-200">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span>Immediate actionable advice</span>
                </li>
                <li className="flex items-start text-gray-200">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span>Schedule at YOUR convenience</span>
                </li>
              </ul>

              <button
                onClick={() => handleUpsellClick('power-session')}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-lg font-bold hover:from-blue-600 hover:to-blue-700 transition-all text-lg uppercase tracking-wide shadow-xl"
              >
                Get Personalized Help Now →
              </button>
            </div>

            {/* Licensing Guide Upsell */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-xl p-6 border-2 border-blue-300">
              <div className="text-center mb-4">
                <h3 className="text-2xl font-bold text-white mb-2">50-State Licensing Guide</h3>
                <p className="text-gray-300 text-sm mb-4">Complete state-by-state licensing requirements guide</p>
                <div className="text-4xl font-black text-blue-400 mb-4">$79</div>
              </div>
              
              <ul className="space-y-3 mb-6 text-sm">
                <li className="flex items-start text-gray-200">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span>All 50 states covered</span>
                </li>
                <li className="flex items-start text-gray-200">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span>Direct links to state health departments</span>
                </li>
                <li className="flex items-start text-gray-200">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span>Certificate of Need (CON) states identified</span>
                </li>
                <li className="flex items-start text-gray-200">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span>Instant digital download</span>
                </li>
              </ul>

              <button
                onClick={() => handleUpsellClick('licensing-guide')}
                className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white py-4 rounded-lg font-bold hover:from-gray-700 hover:to-gray-800 transition-all text-lg uppercase tracking-wide shadow-xl"
              >
                Get Instant Access →
              </button>
            </div>
          </div>

          {/* Skip Option */}
          <div className="text-center">
            <button
              onClick={handleSkipToWebinar}
              className="text-gray-400 hover:text-white font-semibold text-lg underline"
            >
              No thanks, just register me for the free webinar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebinarUpsellModal;
